///
/// EarthBrowser
///
/// Client-Side Weather Computation
///
/// Computes current conditions and daily forecasts from raw grid data.
/// Ported from server-side weather-current.ts and weather-daily.ts.
///
/// Created by Matt Giger on 4/12/2026
/// Copyright (c) 2026 Geodata Labs
///

export interface GridTimestepValues {
	validTime: number
	forecastHour: number
	values: Record<string, number | null>
}

export interface CurrentWeather {
	temp_c: number | null
	dewpoint_c: number | null
	humidity_pct: number | null
	pressure_hpa: number | null
	pressure_trend_3h_hpa: number | null
	wind_speed_mps: number | null
	wind_dir_deg: number | null
	wind_gust_mps: number | null
	cloud_cover_pct: number | null
	precip_rate_mmh: number | null
	feels_like_c: number | null
	weather_code: string
	weather_desc: string
	source: string
	time: string
}

export interface HourlyWeather {
	time: string
	temp_c: number | null
	humidity_pct: number | null
	wind_speed_mps: number | null
	wind_dir_deg: number | null
	wind_gust_mps: number | null
	precip_mm: number | null
	cloud_cover_pct: number | null
	pressure_hpa: number | null
	weather: { code: string, description: string, icon: string }
}

export interface DailyForecast {
	date: string
	temp_min_c: number | null
	temp_max_c: number | null
	humidity_min_pct: number | null
	humidity_max_pct: number | null
	wind_speed_mps: number | null
	wind_dir_deg: number | null
	wind_gust_mps: number | null
	cloud_cover_pct: number | null
	precip_total_mm: number | null
	precip_probability: number | null
	weather: { code: string, description: string, icon: string }
}

function lerp(a: number | null, b: number | null, t: number): number | null {
	if (a === null && b === null) return null
	if (a === null) return b
	if (b === null) return a
	return a + (b - a) * t
}

/**
 * Derive relative humidity from temperature and dewpoint (Magnus formula).
 * The EBWX grids store dewpoint_c but not humidity_pct, so RH must be computed.
 */
export function deriveRelativeHumidity(tempC: number, dewpointC: number): number {
	const a = 17.625
	const b = 243.04
	const rh = 100 * Math.exp((a * dewpointC) / (b + dewpointC) - (a * tempC) / (b + tempC))
	return Math.min(100, Math.max(0, rh))
}

function resolveHumidity(values: Record<string, number | null>): number | null {
	const stored = values.humidity_pct ?? null
	if (stored !== null) return stored
	const temp = values.temp_c ?? null
	const dewpoint = values.dewpoint_c ?? null
	if (temp === null || dewpoint === null) return null
	return deriveRelativeHumidity(temp, dewpoint)
}

/** Sample a variable at an arbitrary time, clamping to the available time range */
function sampleValueAt(
	timesteps: GridTimestepValues[],
	key: string,
	timeSec: number
): { value: number, time: number } | null {
	const usable = timesteps.filter(ts => (ts.values[key] ?? null) !== null)
	if (usable.length === 0) return null

	const first = usable[0]
	const last = usable[usable.length - 1]
	if (timeSec <= first.validTime) return { value: first.values[key]!, time: first.validTime }
	if (timeSec >= last.validTime) return { value: last.values[key]!, time: last.validTime }

	for (let i = 0; i < usable.length - 1; i++) {
		const a = usable[i]
		const b = usable[i + 1]
		if (timeSec >= a.validTime && timeSec <= b.validTime) {
			const t = (timeSec - a.validTime) / (b.validTime - a.validTime)
			return { value: a.values[key]! + (b.values[key]! - a.values[key]!) * t, time: timeSec }
		}
	}
	return null
}

/**
 * 3-hour pressure tendency in hPa (positive = rising).
 * Compares interpolated pressure now vs 3 hours ago, normalized to a 3h rate
 * when the grid does not reach that far back.
 */
function computePressureTrend(timesteps: GridTimestepValues[], nowSec: number): number | null {
	const pNow = sampleValueAt(timesteps, "pressure_hpa", nowSec)
	const pPast = sampleValueAt(timesteps, "pressure_hpa", nowSec - 3 * 3600)
	if (!pNow || !pPast) return null

	const dtSec = pNow.time - pPast.time
	if (dtSec < 3600) return null

	const trend = (pNow.value - pPast.value) / dtSec * 3 * 3600
	return Math.round(trend * 10) / 10
}

export function calculateFeelsLike(
	tempC: number,
	windSpeedMps: number | null,
	humidityPct: number | null
): number {
	const tempF = tempC * 9 / 5 + 32
	const windMph = windSpeedMps !== null ? windSpeedMps * 2.237 : 0
	const rh = humidityPct ?? 50

	let feelsLikeF: number

	if (tempF <= 50 && windMph > 3) {
		feelsLikeF = 35.74 + 0.6215 * tempF -
			35.75 * Math.pow(windMph, 0.16) +
			0.4275 * tempF * Math.pow(windMph, 0.16)
	} else if (tempF >= 80 && rh > 40) {
		feelsLikeF = -42.379 +
			2.04901523 * tempF +
			10.14333127 * rh -
			0.22475541 * tempF * rh -
			0.00683783 * tempF * tempF -
			0.05481717 * rh * rh +
			0.00122874 * tempF * tempF * rh +
			0.00085282 * tempF * rh * rh -
			0.00000199 * tempF * tempF * rh * rh
	} else {
		feelsLikeF = tempF
	}

	return Math.round((feelsLikeF - 32) * 5 / 9 * 10) / 10
}

/** ECMWF precipitation type, WMO GRIB2 code table 4.201 */
export const PrecipType = {
	None: 0,
	Rain: 1,
	FreezingRain: 3,
	Snow: 5,
	WetSnow: 6,
	RainSnowMix: 7,
	IcePellets: 8,
	FreezingDrizzle: 12,
} as const

/** Most-unstable CAPE above which precipitation is called a thunderstorm */
const cThunderstormCapeJkg = 1000

export function deriveWeatherConditions(
	cloudCoverPct: number | null,
	precipRateMmh: number | null,
	precipType: number | null = null,
	capeJkg: number | null = null
): { code: string, description: string, icon: string } {
	if (precipRateMmh !== null && precipRateMmh > 0) {
		// Convective: enough instability for storms and more than a trace
		if (capeJkg !== null && capeJkg >= cThunderstormCapeJkg && precipRateMmh > 0.5) {
			return { code: "thunderstorm", description: "Thunderstorms", icon: "thunderstorm" }
		}

		switch (precipType) {
			case PrecipType.Snow:
			case PrecipType.WetSnow:
				if (precipRateMmh > 2.5) return { code: "heavy_snow", description: "Heavy Snow", icon: "heavy_snow" }
				return { code: "snow", description: "Snow", icon: "snow" }
			case PrecipType.RainSnowMix:
				return { code: "sleet", description: "Rain and Snow", icon: "sleet" }
			case PrecipType.IcePellets:
				return { code: "sleet", description: "Sleet", icon: "sleet" }
			case PrecipType.FreezingRain:
			case PrecipType.FreezingDrizzle:
				return { code: "freezing_rain", description: "Freezing Rain", icon: "freezing_rain" }
		}

		if (precipRateMmh > 7.6) return { code: "heavy_rain", description: "Heavy Rain", icon: "rain" }
		if (precipRateMmh > 2.5) return { code: "rain", description: "Rain", icon: "rain" }
		if (precipRateMmh > 0.5) return { code: "light_rain", description: "Light Rain", icon: "rain" }
		return { code: "drizzle", description: "Drizzle", icon: "drizzle" }
	}

	if (cloudCoverPct === null) return { code: "unknown", description: "Unknown", icon: "unknown" }
	if (cloudCoverPct < 10) return { code: "clear", description: "Clear", icon: "clear" }
	if (cloudCoverPct < 25) return { code: "mostly_clear", description: "Mostly Clear", icon: "mostly_clear" }
	if (cloudCoverPct < 50) return { code: "partly_cloudy", description: "Partly Cloudy", icon: "partly_cloudy" }
	if (cloudCoverPct < 75) return { code: "mostly_cloudy", description: "Mostly Cloudy", icon: "mostly_cloudy" }
	return { code: "cloudy", description: "Cloudy", icon: "cloudy" }
}

export function computeCurrentWeather(
	timesteps: GridTimestepValues[],
	atSec: number = Date.now() / 1000
): CurrentWeather | null {
	if (timesteps.length === 0) return null

	// Sample time: defaults to the real present, but callers may pass the
	// scene clock so displays follow the time slider. Clamped implicitly to
	// the grid's time range (values freeze at the first/last timestep).
	const nowSec = atSec

	let before: GridTimestepValues | null = null
	let after: GridTimestepValues | null = null

	for (const ts of timesteps) {
		if (ts.validTime <= nowSec) before = ts
		else if (!after) after = ts
	}

	if (!before && !after) return null

	let values: Record<string, number | null>

	if (before && after) {
		const t = Math.max(0, Math.min(1, (nowSec - before.validTime) / (after.validTime - before.validTime)))
		values = {}
		const keys = new Set([...Object.keys(before.values), ...Object.keys(after.values)])
		for (const key of keys) {
			values[key] = lerp(before.values[key] ?? null, after.values[key] ?? null, t)
		}
		// Precipitation type is a category: take the nearer timestep's, but
		// prefer whichever side actually has precipitation so a shower that
		// starts between samples still gets its type
		const nearType = (t < 0.5 ? before : after).values.ptype ?? null
		const farType = (t < 0.5 ? after : before).values.ptype ?? null
		values.ptype = nearType !== null && nearType !== PrecipType.None ? nearType : (farType ?? nearType)
	} else {
		values = { ...(before ?? after)!.values }
	}

	const windU = values.wind_u_mps ?? null
	const windV = values.wind_v_mps ?? null
	let windSpeedMps: number | null = null
	let windDirDeg: number | null = null

	if (windU !== null && windV !== null) {
		windSpeedMps = Math.sqrt(windU * windU + windV * windV)
		windDirDeg = (Math.atan2(windU, windV) * 180 / Math.PI + 180) % 360
	}

	const tempC = values.temp_c ?? null
	const dewpoint = values.dewpoint_c ?? null
	const humidity = resolveHumidity(values)
	const pressure = values.pressure_hpa ?? null
	const gust = values.wind_gust_mps ?? null
	const cloudCover = values.cloud_cover_pct ?? null
	const precip = values.precip_total_mm ?? null
	const feelsLike = tempC !== null ? calculateFeelsLike(tempC, windSpeedMps, humidity) : null

	const weather = deriveWeatherConditions(cloudCover, precip, values.ptype ?? null, values.mucape_jkg ?? null)

	return {
		temp_c: tempC !== null ? Math.round(tempC * 10) / 10 : null,
		dewpoint_c: dewpoint !== null ? Math.round(dewpoint * 10) / 10 : null,
		humidity_pct: humidity !== null ? Math.round(humidity) : null,
		pressure_hpa: pressure !== null ? Math.round(pressure * 10) / 10 : null,
		pressure_trend_3h_hpa: computePressureTrend(timesteps, nowSec),
		wind_speed_mps: windSpeedMps !== null ? Math.round(windSpeedMps * 10) / 10 : null,
		wind_dir_deg: windDirDeg !== null ? Math.round(windDirDeg) : null,
		wind_gust_mps: gust !== null ? Math.round(gust * 10) / 10 : null,
		cloud_cover_pct: cloudCover !== null ? Math.round(cloudCover) : null,
		precip_rate_mmh: precip !== null ? Math.round(precip * 100) / 100 : null,
		feels_like_c: feelsLike,
		weather_code: weather.code,
		weather_desc: weather.description,
		source: "ecmwf_grid",
		time: new Date(nowSec * 1000).toISOString(),
	}
}

/**
 * Per-timestep forecast entries (grid timesteps are ~3-hourly).
 * Filters out timesteps more than 30 minutes in the past.
 */
export function computeHourlyForecast(timesteps: GridTimestepValues[]): HourlyWeather[] {
	const nowSec = Date.now() / 1000

	return timesteps
		.filter(ts => ts.validTime >= nowSec - 1800)
		.map(ts => {
			const values = ts.values
			const windU = values.wind_u_mps ?? null
			const windV = values.wind_v_mps ?? null
			let windSpeed: number | null = null
			let windDir: number | null = null
			if (windU !== null && windV !== null) {
				windSpeed = Math.round(Math.sqrt(windU * windU + windV * windV) * 10) / 10
				windDir = Math.round((Math.atan2(windU, windV) * 180 / Math.PI + 180) % 360)
			}

			const temp = values.temp_c ?? null
			const humidity = resolveHumidity(values)
			const precip = values.precip_total_mm ?? null
			const cloudCover = values.cloud_cover_pct ?? null
			const pressure = values.pressure_hpa ?? null

			return {
				time: new Date(ts.validTime * 1000).toISOString(),
				temp_c: temp !== null ? Math.round(temp * 10) / 10 : null,
				humidity_pct: humidity !== null ? Math.round(humidity) : null,
				wind_speed_mps: windSpeed,
				wind_dir_deg: windDir,
				wind_gust_mps: values.wind_gust_mps ?? null,
				precip_mm: precip !== null ? Math.round(precip * 100) / 100 : null,
				cloud_cover_pct: cloudCover !== null ? Math.round(cloudCover) : null,
				pressure_hpa: pressure !== null ? Math.round(pressure * 10) / 10 : null,
				weather: deriveWeatherConditions(cloudCover, precip, values.ptype ?? null, values.mucape_jkg ?? null),
			}
		})
}

// ENS precipitation probabilities describe 24 h windows ending at the
// timestep they are stored on (every 12 h). A local calendar day takes the
// window whose end is closest to local midnight, within half a stride.
const cProbWindowMaxOffsetSec = 12 * 3600

/**
 * Ensemble "chance of rain" for a local day from the prob_tp_1mm planes,
 * null when the shard carries no ENS data near that day
 */
function ensemblePrecipProbability(timesteps: GridTimestepValues[], dateKey: string): number | null {
	const dayEndSec = new Date(`${dateKey}T00:00:00`).getTime() / 1000 + 86400
	let best: { value: number, offset: number } | null = null
	for (const ts of timesteps) {
		const prob = ts.values.prob_tp_1mm ?? null
		if (prob === null) continue
		const offset = Math.abs(ts.validTime - dayEndSec)
		if (offset <= cProbWindowMaxOffsetSec && (!best || offset < best.offset)) {
			best = { value: prob, offset }
		}
	}
	return best ? Math.round(best.value) : null
}

/** Most common non-zero precipitation type among the given timesteps */
function dominantPrecipType(hours: GridTimestepValues[]): number | null {
	const counts = new Map<number, number>()
	for (const h of hours) {
		const type = h.values.ptype ?? null
		const precip = h.values.precip_total_mm ?? 0
		if (type === null || type === PrecipType.None || precip <= 0) continue
		counts.set(type, (counts.get(type) ?? 0) + 1)
	}
	let bestType: number | null = null
	let bestCount = 0
	for (const [type, count] of counts) {
		if (count > bestCount) {
			bestType = type
			bestCount = count
		}
	}
	return bestType
}

export function computeDailyForecasts(
	timesteps: GridTimestepValues[],
	days: number = 7
): DailyForecast[] {
	if (timesteps.length === 0) return []

	const now = new Date()
	const todayKey = localDateKey(now)

	const byDate = new Map<string, GridTimestepValues[]>()

	for (const ts of timesteps) {
		const date = new Date(ts.validTime * 1000)
		const dateKey = localDateKey(date)
		if (dateKey < todayKey) continue
		const group = byDate.get(dateKey) ?? []
		group.push(ts)
		byDate.set(dateKey, group)
	}

	const sortedDates = [...byDate.keys()].sort().slice(0, days)
	const results: DailyForecast[] = []

	for (const dateKey of sortedDates) {
		const hours = byDate.get(dateKey)!

		const temps = hours.map(h => h.values.temp_c).filter((v): v is number => v !== null)
		// Period max/min since the previous step catch extremes that fall
		// between the 3-6 hourly instantaneous samples
		const periodMaxTemps = hours.map(h => h.values.temp_max_c ?? null).filter((v): v is number => v !== null)
		const periodMinTemps = hours.map(h => h.values.temp_min_c ?? null).filter((v): v is number => v !== null)
		const highs = [...temps, ...periodMaxTemps]
		const lows = [...temps, ...periodMinTemps]
		const humidities = hours.map(h => resolveHumidity(h.values)).filter((v): v is number => v !== null)
		const capes = hours.map(h => h.values.mucape_jkg ?? null).filter((v): v is number => v !== null)
		const gusts = hours.map(h => h.values.wind_gust_mps).filter((v): v is number => v !== null)
		const precipRates = hours.map(h => h.values.precip_total_mm).filter((v): v is number => v !== null)

		let windSpeed: number | null = null
		let windDir: number | null = null
		const windUs = hours.map(h => h.values.wind_u_mps).filter((v): v is number => v !== null)
		const windVs = hours.map(h => h.values.wind_v_mps).filter((v): v is number => v !== null)
		if (windUs.length > 0 && windVs.length > 0) {
			const avgU = windUs.reduce((a, b) => a + b, 0) / windUs.length
			const avgV = windVs.reduce((a, b) => a + b, 0) / windVs.length
			windSpeed = Math.round(Math.sqrt(avgU * avgU + avgV * avgV) * 10) / 10
			windDir = Math.round((Math.atan2(avgU, avgV) * 180 / Math.PI + 180) % 360)
		}

		// Daytime cloud cover (06-18 UTC)
		const daytimeClouds = hours
			.filter(h => {
				const hr = new Date(h.validTime * 1000).getUTCHours()
				return hr >= 6 && hr <= 18
			})
			.map(h => h.values.cloud_cover_pct)
			.filter((v): v is number => v !== null)

		const avgCloud = daytimeClouds.length > 0
			? Math.round(daytimeClouds.reduce((a, b) => a + b, 0) / daytimeClouds.length)
			: null

		// Precipitation: estimate total assuming ~3h per timestep
		const precipTotal = precipRates.length > 0
			? Math.round(precipRates.reduce((a, b) => a + b, 0) * 3 * 100) / 100
			: null

		// Chance of rain: ENS probability of >= 1 mm when the shard carries
		// it, else the fraction of deterministic steps with measurable precip
		const ensembleProb = ensemblePrecipProbability(timesteps, dateKey)
		const precipProb = ensembleProb ?? (precipRates.length > 0
			? Math.round((precipRates.filter(r => r > 0.1).length / precipRates.length) * 100)
			: null)

		const maxPrecipRate = precipRates.length > 0 ? Math.max(...precipRates) : null
		const maxCape = capes.length > 0 ? Math.max(...capes) : null
		const weather = deriveWeatherConditions(avgCloud, maxPrecipRate, dominantPrecipType(hours), maxCape)

		results.push({
			date: dateKey,
			temp_min_c: lows.length > 0 ? Math.round(Math.min(...lows) * 10) / 10 : null,
			temp_max_c: highs.length > 0 ? Math.round(Math.max(...highs) * 10) / 10 : null,
			humidity_min_pct: humidities.length > 0 ? Math.round(Math.min(...humidities)) : null,
			humidity_max_pct: humidities.length > 0 ? Math.round(Math.max(...humidities)) : null,
			wind_speed_mps: windSpeed,
			wind_dir_deg: windDir,
			wind_gust_mps: gusts.length > 0 ? Math.round(Math.max(...gusts) * 10) / 10 : null,
			cloud_cover_pct: avgCloud,
			precip_total_mm: precipTotal,
			precip_probability: precipProb,
			weather,
		})
	}

	return results
}

function localDateKey(d: Date): string {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
